import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const API = "https://eps.elkomprintama.com/";
const ALIGN_KEY = "eps_jobsheet_align";

// A4 preview scale
const SCALE = 0.5;
const A4_W = Math.round(794 * SCALE);
const A4_H = Math.round(1123 * SCALE);
const toPx = (vbCoord, offsetMm = 0) =>
  (vbCoord * 0.254 + offsetMm) * 3.7795 * SCALE;
const EL_W = Math.round(81 * 0.254 * 3.7795 * SCALE);
const EL_H = Math.round(45 * 0.254 * 3.7795 * SCALE);
const FONT = Math.round(14 * 0.352778 * 3.7795 * SCALE);
const FONT_SM = Math.round(10 * 0.352778 * 3.7795 * SCALE);
const FONT_LG = Math.round(20 * 0.352778 * 3.7795 * SCALE);

const FIELDS = [
  // key, label, base VB X, base VB Y, color
  {
    key: "_global",
    label: "Global (all)",
    vbX: null,
    vbY: null,
    color: "#f59e0b",
    isGlobal: true,
  },
  { key: "job_id", label: "Job ID", vbX: 380, vbY: 100, color: "#7c3aed" },
  { key: "job_date", label: "Date", vbX: 145, vbY: 185, color: "#2563eb" },
  { key: "nme", label: "Customer", vbX: 145, vbY: 222, color: "#2563eb" },
  {
    key: "job_name",
    label: "Model Name",
    vbX: 145,
    vbY: 258,
    color: "#2563eb",
  },
  { key: "mat_name", label: "Material", vbX: 145, vbY: 295, color: "#2563eb" },
  {
    key: "mat_clr",
    label: "Mat. Colour",
    vbX: 145,
    vbY: 329,
    color: "#2563eb",
  },
  { key: "size", label: "Size", vbX: 145, vbY: 368, color: "#2563eb" },
  { key: "ptg", label: "PTG", vbX: 145, vbY: 408, color: "#2563eb" },
  { key: "pn_cust", label: "P/N Cust", vbX: 145, vbY: 444, color: "#2563eb" },
  { key: "mpn_elk", label: "MPN Elkom", vbX: 145, vbY: 481, color: "#2563eb" },
  { key: "drw_no", label: "Drawing No", vbX: 145, vbY: 515, color: "#2563eb" },
  { key: "die_no", label: "Die Cut No", vbX: 145, vbY: 554, color: "#2563eb" },
  { key: "roll", label: "Roll", vbX: 562, vbY: 293, color: "#059669" },
  { key: "core", label: "Core", vbX: 562, vbY: 334, color: "#059669" },
  {
    key: "ellipse_fi",
    label: "Ellipse (Face In)",
    vbX: 585,
    vbY: 366,
    color: "#059669",
    isEllipse: true,
  },
  {
    key: "ellipse_fo",
    label: "Ellipse (Face Out)",
    vbX: 545,
    vbY: 366,
    color: "#0891b2",
    isEllipse: true,
  },
  {
    key: "sht_val",
    label: "Sheet (value)",
    vbX: 542,
    vbY: 437,
    color: "#059669",
  },
  {
    key: "cmp_label",
    label: "Comp. Label",
    vbX: 622,
    vbY: 474,
    color: "#059669",
  },
  {
    key: "std_pack",
    label: "Std Packing",
    vbX: 636,
    vbY: 518,
    color: "#059669",
  },
  {
    key: "pefo_yes",
    label: "Pefo X (Peforated)",
    vbX: 450,
    vbY: 560,
    color: "#059669",
  },
  {
    key: "pefo_no",
    label: "Pefo X (No Peforated)",
    vbX: 610,
    vbY: 560,
    color: "#dc2626",
  },
  { key: "required", label: "Required", vbX: 600, vbY: 632, color: "#059669" },
  { key: "lami", label: "Lamination", vbX: 600, vbY: 662, color: "#059669" },
  { key: "admin", label: "Prepare By", vbX: 43, vbY: 730, color: "#78716c" },
  { key: "note", label: "Notes", vbX: 22, vbY: 875, color: "#78716c" },
];

const mkZero = () =>
  Object.fromEntries(FIELDS.map((f) => [f.key, { dx: 0, dy: 0 }]));

function NumInput({ value, onChange }) {
  return (
    <input
      type="number"
      step="0.1"
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      className="w-16 border border-gray-300 rounded px-1 py-0.5 text-xs text-center font-mono focus:outline-none focus:border-indigo-400"
    />
  );
}

const AlignJobsheet = () => {
  const [offsets, setOffsets] = useState(mkZero);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}get/api/print-align`, { withCredentials: true })
      .then((res) => {
        const data = res.data.result || {};
        setOffsets((prev) => {
          const next = { ...prev };
          Object.keys(data).forEach((k) => {
            if (next[k]) next[k] = data[k];
          });
          return next;
        });
      })
      .catch(() => {
        // Fall back to localStorage
        try {
          const cached = JSON.parse(localStorage.getItem(ALIGN_KEY) || "{}");
          if (Object.keys(cached).length)
            setOffsets((prev) => ({ ...prev, ...cached }));
        } catch (_) {}
      })
      .finally(() => setLoading(false));
  }, []);

  const set = useCallback((key, axis, val) => {
    setOffsets((prev) => ({ ...prev, [key]: { ...prev[key], [axis]: val } }));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const fields = Object.entries(offsets).map(([key, { dx, dy }]) => ({
      key,
      dx,
      dy,
    }));
    try {
      await axios.post(
        `${API}post/api/print-align`,
        { fields },
        { withCredentials: true },
      );
      localStorage.setItem(ALIGN_KEY, JSON.stringify(offsets));
      toast.success("Alignment saved!");
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setOffsets(mkZero());
    localStorage.removeItem(ALIGN_KEY);
    toast.info("Reset to default. Click Save to persist.");
  };

  // Effective position in display px for a field
  const ex = (f) => {
    if (!f.vbX) return 0;
    const g = offsets["_global"] || { dx: 0, dy: 0 };
    const p = offsets[f.key] || { dx: 0, dy: 0 };
    return toPx(f.vbX, g.dx + p.dx);
  };
  const ey = (f) => {
    if (!f.vbY) return 0;
    const g = offsets["_global"] || { dx: 0, dy: 0 };
    const p = offsets[f.key] || { dy: 0 };
    return toPx(f.vbY, g.dy + p.dy);
  };

  const nonGlobal = FIELDS.filter((f) => !f.isGlobal);

  if (loading)
    return (
      <div className="text-center py-10 text-gray-400 text-sm">
        Loading alignment…
      </div>
    );

  return (
    <div className="flex flex-col" style={{ maxHeight: "calc(100vh - 80px)" }}>
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-4 flex-shrink-0">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-bold text-gray-800">
              Jobsheet Print Alignment
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Adjust offsets (mm) so printed values land on the correct boxes of
              the pre-printed form. <strong>Global</strong> shifts everything;
              per-field rows fine-tune individual values. Set browser print
              margins to <strong>None</strong>.
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={handleReset}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 bg-white border border-gray-300 hover:bg-gray-100 transition"
            >
              Reset All
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2 rounded-lg text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 transition"
            >
              {saving ? "Saving…" : "Save to DB"}
            </button>
          </div>
        </div>
      </div>

      {/* Main: table + preview */}
      <div className="flex gap-4 flex-1 min-h-0">
        {/* Left column: field table + tip */}
        <div className="flex-1 min-h-0 flex flex-col gap-3">
          {/* Field table */}
          <div className="flex-1 min-h-0 bg-white border border-gray-200 rounded-xl shadow-sm overflow-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-3 py-2 font-semibold text-gray-500">
                    Field
                  </th>
                  <th className="text-center px-2 py-2 font-semibold text-gray-400 w-20">
                    Base X
                  </th>
                  <th className="text-center px-2 py-2 font-semibold text-gray-400 w-20">
                    Base Y
                  </th>
                  <th className="text-center px-2 py-2 font-semibold text-indigo-600 w-20">
                    Δ X (mm)
                  </th>
                  <th className="text-center px-2 py-2 font-semibold text-indigo-600 w-20">
                    Δ Y (mm)
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Global row pinned at top */}
                {FIELDS.filter((f) => f.isGlobal).map((f) => (
                  <tr
                    key={f.key}
                    className="border-b-2 border-amber-200 bg-amber-50"
                  >
                    <td className="px-3 py-2 font-bold text-amber-700">
                      {f.label}
                    </td>
                    <td className="px-2 py-2 text-center text-gray-300">—</td>
                    <td className="px-2 py-2 text-center text-gray-300">—</td>
                    <td className="px-2 py-2 text-center">
                      <NumInput
                        value={offsets[f.key]?.dx ?? 0}
                        onChange={(v) => set(f.key, "dx", v)}
                      />
                    </td>
                    <td className="px-2 py-2 text-center">
                      <NumInput
                        value={offsets[f.key]?.dy ?? 0}
                        onChange={(v) => set(f.key, "dy", v)}
                      />
                    </td>
                  </tr>
                ))}

                {nonGlobal.map((f, i) => (
                  <tr
                    key={f.key}
                    className={
                      i % 2 === 0
                        ? "bg-white border-b border-gray-100"
                        : "bg-gray-50 border-b border-gray-100"
                    }
                  >
                    <td className="px-3 py-1.5">
                      <span
                        className="font-semibold"
                        style={{ color: f.color }}
                      >
                        {f.label}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-center text-gray-400 font-mono">
                      {(f.vbX * 0.254).toFixed(1)}
                    </td>
                    <td className="px-2 py-1.5 text-center text-gray-400 font-mono">
                      {(f.vbY * 0.254).toFixed(1)}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <NumInput
                        value={offsets[f.key]?.dx ?? 0}
                        onChange={(v) => set(f.key, "dx", v)}
                      />
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <NumInput
                        value={offsets[f.key]?.dy ?? 0}
                        onChange={(v) => set(f.key, "dy", v)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Tip */}
          <div className="flex-shrink-0 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
            <strong>Tip:</strong> Print a test page on plain paper, hold it over
            the pre-printed form against a light. Measure the gap for each field
            and enter it as Δ X / Δ Y. Positive X = move right, Positive Y =
            move down.
          </div>
        </div>
        {/* end left column */}

        {/* A4 Preview */}
        <div className="flex-shrink-0 overflow-y-auto">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Live Preview
          </p>
          <div
            style={{
              position: "relative",
              width: A4_W,
              height: A4_H,
              background: "#fff",
              border: "1px solid #d1d5db",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            {/* Job ID */}
            {(() => {
              const f = FIELDS.find((x) => x.key === "job_id");
              return (
                <span
                  style={{
                    position: "absolute",
                    left: ex(f),
                    top: ey(f),
                    fontSize: FONT_LG,
                    color: f.color,
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                    lineHeight: 1,
                  }}
                >
                  JOB-ID
                </span>
              );
            })()}

            {/* All text fields (exclude ellipses, note, job_id — handled separately) */}
            {nonGlobal
              .filter(
                (f) => !f.isEllipse && f.key !== "note" && f.key !== "job_id",
              )
              .map((f) => (
                <span
                  key={f.key}
                  style={{
                    position: "absolute",
                    left: ex(f),
                    top: ey(f),
                    fontSize: f.key === "admin" ? FONT_SM : FONT,
                    color: f.color,
                    whiteSpace: "nowrap",
                    lineHeight: 1,
                  }}
                >
                  {f.label}
                </span>
              ))}

            {/* Three ellipses: Face In, Face Out, Sheet */}
            {nonGlobal
              .filter((f) => f.isEllipse)
              .map((f) => (
                <div
                  key={f.key}
                  style={{
                    position: "absolute",
                    left: ex(f),
                    top: ey(f),
                    width: EL_W,
                    height: EL_H,
                    border: `1.5px solid ${f.color}`,
                    borderRadius: "50%",
                  }}
                />
              ))}

            {/* Note block */}
            {(() => {
              const f = FIELDS.find((x) => x.key === "note");
              return (
                <div
                  style={{
                    position: "absolute",
                    left: ex(f),
                    top: ey(f),
                    width: Math.round(230 * 0.254 * 3.7795 * SCALE),
                    height: Math.round(183 * 0.254 * 3.7795 * SCALE),
                    border: "1px dashed #d1d5db",
                    fontSize: FONT * 0.8,
                    color: f.color,
                    padding: 2,
                    lineHeight: 1,
                  }}
                >
                  Notes
                </div>
              );
            })()}
          </div>
        </div>
      </div>
      {/* end main row */}
    </div>
  );
};

export default AlignJobsheet;
